import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Ensure cookies are sent with every request
axios.defaults.withCredentials = true;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const initAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    initAuth();
  }, [initAuth]);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    const { user: userData } = response.data;
    setUser(userData);
    return userData;
  }, []);

  const register = useCallback(async (name, email, password, company_name, invite_code = null) => {
    const payload = { name, email, password, company_name };
    if (invite_code) {
      payload.invite_code = invite_code;
    }
    // Pick up marketing referral tag (`?ref=…`) parked on the landing page.
    try {
      const ref = sessionStorage.getItem('fc_ref');
      if (ref) payload.ref = ref;
    } catch { /* ignore private-mode storage errors */ }
    const response = await axios.post(`${API_URL}/api/auth/register`, payload);
    // Public main signups now require an emailed 6-digit code before login.
    // Return a flag so the caller can redirect the user to /verify-email.
    if (response.data?.status === 'verification_required') {
      return { verificationRequired: true, email: response.data.email };
    }
    const { user: userData, email_verification: emailVerification } = response.data;
    setUser(userData);
    return { ...userData, emailVerification };
  }, []);

  const verifyEmail = useCallback(async (email, code) => {
    const response = await axios.post(`${API_URL}/api/auth/verify-email`, { email, code });
    const { user: userData } = response.data;
    setUser(userData);
    return userData;
  }, []);

  const resendVerification = useCallback(async (email) => {
    const response = await axios.post(`${API_URL}/api/auth/resend-verification`, { email });
    return response.data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`);
    } catch {
      // Ignore logout API errors
    }
    setUser(null);
  }, []);

  const isMainUser = useCallback(() => user?.role === 'main_user', [user]);
  const canEdit = useCallback(() => user?.role === 'main_user' || user?.access_level === 'full_access', [user]);
  const canDelete = useCallback(() => user?.role === 'main_user' || user?.access_level === 'full_access', [user]);
  const canManageTeam = useCallback(() => user?.role === 'main_user', [user]);

  const refreshUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
      return response.data;
    } catch {
      return null;
    }
  }, []);

  const contextValue = useMemo(() => ({
    user, loading, login, register, logout, refreshUser,
    verifyEmail, resendVerification,
    isMainUser, canEdit, canDelete, canManageTeam
  }), [user, loading, login, register, logout, refreshUser, verifyEmail, resendVerification, isMainUser, canEdit, canDelete, canManageTeam]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
